import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { DropdownSearchItem } from '@/modules/common/components/molecules/selectors/dropdown';

/**
 * Project selector used by the time tracker views.
 *
 * Two visual variants:
 * - `inline` (default) — compact outline chip, sits alongside other elements.
 *   Used by FlatView (page header) so it doesn't push siblings around.
 * - `field` — full-width bordered field, reads as a form input.
 *   Used by CascadeView (sidebar panel) as the primary CTA.
 *
 * When a project is selected, the dot always shows the project's color so
 * the user can identify which project is tracked at a glance. Tracker state
 * (running / paused) is communicated by the timer text + button icons, not
 * by the dot color.
 */
interface TimeTrackingProjectSelectorProps {
  selectedProject: DropdownSearchItem | null;
  onClick: () => void;
  variant?: 'inline' | 'field';
}

export const TimeTrackingProjectSelector = ({
  selectedProject,
  onClick,
  variant = 'inline',
}: TimeTrackingProjectSelectorProps) => {
  const { t } = useTranslation(['authenticated.timeTracking', 'common']);

  const isField = variant === 'field';
  const hasProject = !!selectedProject;

  // Inline style is only applied when the project has a real colorHex.
  // The Tailwind fallback class guarantees the dot stays visible even when
  // the API omits colorHex, instead of leaving an empty/transparent circle.
  const dotStyle = selectedProject?.colorHex
    ? { backgroundColor: selectedProject.colorHex }
    : undefined;

  if (isField) {
    return (
      <Button
        type="button"
        variant="outline"
        onClick={onClick}
        className="h-9 w-full justify-start gap-2 overflow-hidden px-3 font-normal"
      >
        <div style={dotStyle} className="h-2.5 w-2.5 shrink-0 rounded-full bg-muted-foreground" />
        <span className={cn('truncate text-sm', !hasProject && 'text-muted-foreground')}>
          {hasProject ? selectedProject?.name : t('tracker.selectProject')}
        </span>
      </Button>
    );
  }

  // Inline variant — compact chip for FlatView and other horizontal layouts.
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={onClick}
      className="h-7 w-fit max-w-full justify-start gap-1.5 overflow-hidden px-2 text-xs font-normal"
    >
      <div
        style={dotStyle}
        className="h-[7px] w-[7px] min-w-[7px] shrink-0 rounded-full bg-muted-foreground"
      />
      <span className={cn('truncate', !hasProject && 'font-medium text-primary')}>
        {hasProject ? selectedProject?.name : t('tracker.selectProject')}
      </span>
    </Button>
  );
};
